import React, { useEffect } from "react";
import { View, Text, StyleSheet, Image, TextInput, TouchableOpacity } from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import Colors from "../../../../../constants/colors";
import GradientBackground from '../../../../uiComponents/GradientBackground';
import { postFeeLookup } from "../../../../../services/billpay.service";

type Props = {
  onValidationChange?: (valid: boolean) => void;
  onContinue?: () => void;
};

export default function PayAmountStep({ onValidationChange, onContinue }: Props) {
  const { t } = useTranslation();
  const sendAmount = useSessionStore((s: SessionState) => s.currentSendAmount);
  const setCurrentSendAmount = useSessionStore((s: SessionState) => (s as any).setCurrentSendAmount);
  const billerSearchResp = useSessionStore((s: SessionState) => (s as any).billPayBillerSearchResponseStore);
  const setBillPayFeeLookupResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayFeeLookupResponseStore);

  const isValid = sendAmount !== "0.00";
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  const parseAmount = (text: string): string => {
    // allow only digits and one optional decimal point
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  const handleContinue = async () => {
    try {
      const billerInfo: any = (billerSearchResp?.BillerInfo ?? billerSearchResp?.billerInfo ?? billerSearchResp) as any;
      const receiveCode = billerInfo?.ReceiveCode ?? billerInfo?.receiveCode ?? undefined;
      const receiveAgentID = billerInfo?.ReceiveAgentID ?? billerInfo?.receiveAgentID ?? undefined;
      const payload = {
        productType: 'BP',
        productVariant: 'EP',
        amountExcludingFee: sendAmount,
        destinationCountry: 'USA',
        receiveCode,
        receiveAgentID,
        receiveCurrency: 'USD',
        allOptions: false,
      };
      const resp = await postFeeLookup(payload);
      try { setBillPayFeeLookupResponseStore?.(resp); } catch {}
    } catch (e) {
      console.warn('Fee lookup failed', e);
    }
  };

  return (
    <View style={styles.container}>
      <View style={{ alignItems: 'center'}}>
        <Text style={styles.title}>{t("PAY_AMOUNT")}</Text>
        <View style={styles.amountInput}>
          <TextInput
            testID="billPayAmount"
            autoFocus
            style={styles.amountText}
            keyboardType="decimal-pad"
            placeholder="0.00"
            value={sendAmount}
            onChangeText={(t) => {
              const next = parseAmount(t);
              // setSendAmount(next);
              setCurrentSendAmount?.(next);
            }}
          />
          <View style={{ marginLeft: 8, alignItems: 'center', justifyContent: 'center'}}>
            <Image source={EnglishFlag} style={{ width: 16, height: 16}}/>
            <Text style={styles.currencyText}>USD</Text>
          </View>
        </View>
        <Text style={[styles.subtitle, {marginBottom: 16}]}>{t("PAYMENT_RECEIVED_INFORMATION")}</Text>
        <Text style={[styles.subtitle, {paddingLeft: 5}]}>{t("PAYMENT_POST")}</Text>
      </View>

      {/* <View style={styles.footerWrapper}> */}
      <GradientBackground style={styles.gradientCard}>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_TOTAL")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{sendAmount}USD</Text>
          </View>
        </View>
        <TouchableOpacity
          onPress={onContinue}
          activeOpacity={0.8}
          style={styles.continueButton}
        >
          <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
        </TouchableOpacity>
      </GradientBackground>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    marginBottom: 350
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 40,
    fontWeight: "500",
    textAlign: "center",
    color: Colors.titleTextColor,
    lineHeight: 46,
    width: 200
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
    color: Colors.subTitleColor,
    lineHeight: 22
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 32,
    paddingBottom: 40,
  },
  title: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    textAlign: "center",
    color: Colors.subTitleColor,
    lineHeight: 24,
    marginTop: 32,
  },
  subtitle: {
    fontFamily: 'Rubik',
    fontSize: 12,
    fontWeight: "400",
    textAlign: "left",
    color: Colors.infoSubValue,
    paddingLeft: 24,
    paddingRight: 90,
    lineHeight: 16
  },
    card: {
    alignItems: "center",
    marginHorizontal: 24
    // width: 327,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  continueText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600'
  },
  footerWrapper: {
    position: "relative",
    flex: 1,
    justifyContent: "flex-end",
  },
  gradientCard: {
    paddingHorizontal: 24,
    paddingVertical: 28,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#111",
  },
  oldPrice: {
    fontSize: 16,
    fontWeight: "600",
  },
  continueButton: {
    alignSelf: "center",
    backgroundColor: "#000",
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: "#000",
  },
  continueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
    lineHeight: 24
  },
});